import React, { useCallback, useMemo, useState } from 'react'
import FormQuestion from './createQuestion.component'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import questionnaireCreateQuestionnaireDefinition from './create/questionnaireCreate.definition'
import styled from 'styled-components'
import StringField from '~/prix/react/components/form/field/stringField'
import DateField from '~/prix/react/components/form/field/dateTimeField'
import { Button } from '~/prix/react/components/button'
import { toastError, toastSuccess } from '~/components/toasts'
import { format } from 'date-fns'
import { NewFieldProps, NewQuestion, QuestionnaireDataType } from './questionnaireTypes'
import EnumeratedField from '~/prix/react/components/form/field/enumeratedField'
import Card from '~/components/card'
import { useNavigate } from 'react-router'
import { getCitizenPublicTargets } from './questionnairePublicTargets.utils'
import { colors } from '~/design'
import { enumerated } from '~/prix'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.padding(2)};
  margin-bottom: ${({ theme }) => theme.padding(2)};

  .questionary {
    display: flex;
    width: 100%;
    padding: 0 ${({ theme }) => theme.padding(3)} ${({ theme }) => theme.padding(4)} ${({ theme }) => theme.padding(3)};
    align-items: center;

    .questionary-column {
      display: flex;
      width: 100%;
      align-items: center;
      flex-direction: column;

      h2 {
        text-align: center;
        margin-bottom: ${({ theme }) => theme.padding(2)};
      }

      form {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.padding(2)};
        padding: 10px 15px;
      }

      .custom-select__control {
        height: 45px;
        border-radius: 8px;
        padding: 0.8rem 1rem;
        margin: 4px 0px 2px 0px;
      }

      .react-datepicker-wrapper {
        width: 100%;
      }

      input {
        flex: 1;
        width: 100%;
      }
    }
  }

  .button-questionary {
    padding: ${({ theme }) => theme.padding(2)} 0 ${({ theme }) => theme.padding(3)} 0;
    display: flex;
    justify-content: center;
    width: 100%;
    
    button {
      height: 45px;
      min-width: 160px;
      background-color: ${colors.prixOrange};
      border-radius: 8px;
      font-weight: 700;
    }
  }
`

const CreateQuestionnaire = () => {
  const navigate = useNavigate()
  const { publicTargets, publicTargetsEnum } = useMemo(() => {
    const publicTargets = getCitizenPublicTargets()
    const publicTargetsEnum = enumerated(
      publicTargets.reduce((acc, target) => {
        acc[target.value] = target.label
        return acc
      }, {} as Record<string, string>),
    )

    return { publicTargets, publicTargetsEnum }
  }, [])
  const [formFields, setFormFields] = useState<NewQuestion[]>([])

  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireDataType>({
    id: null,
    title: '',
    observation: '',
    targetPublic: null,
    targetType: 'citizen',
    delimitationType: null,
    delimitationId: null,
    delimitationCustom: null,
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: null,
    lgpdDisclaimer:
      'As informações coletadas neste questionário serão usadas exclusivamente para análise e planejamento de melhorias nos serviços públicos. Garantimos a confidencialidade e segurança de seus dados, conforme estabelecido pela Lei Geral de Proteção de Dados (LGPD). Quaisquer dados publicados estarão devidamente anonimizados. Ao responder, você concorda com o uso desses dados para os fins mencionados.',
    deletedAt: null,
  })

  const [newField, setNewField] = useState<NewFieldProps | null>({
    title: '',
    observation: null,
    type: '',
    required: true,
    ordinalPosition: 1,
    deletedAt: null,
    questionOptions: [],
    childQuestions: null,
  })

  const handleFormFieldsChange = (newInput: any) => {
    setFormFields([...formFields, newInput])
  }

  const { callAction } = useActionFunction(questionnaireCreateQuestionnaireDefinition)

  const handleSubmit = useCallback(
    async (event: React.MouseEvent, data: any) => {
      event.preventDefault()

      const updatedFields = [...formFields, newField]
      const questionnaire = {
        ...data,
        targetPublic: questionnaireData.targetPublic
          ? JSON.stringify(
              publicTargets.find(target => target.value === questionnaireData.targetPublic),
            )
          : null,
        questions: updatedFields,
      }
      const { resultPromise } = callAction(questionnaire)
      resultPromise
        .then(result => {
          console.log('Questionário criado com sucesso:', result)
          toastSuccess('Questionário criado com sucesso!')
          navigate('/app/questionnaire/list', { replace: true })
        })
        .catch(err => {
          console.log('Erro ao criar questionário:', err.message)
          toastError(err.message || 'Erro ao criar questionário')
        })
    },
    [questionnaireData, formFields, publicTargets, newField],
  )

  const handleInputChange = (type: string, value: string | number | null) => {
    setQuestionnaireData(prevData => ({
      ...prevData,
      [type]: value,
    }))
  }
  return (
    <Card title='Crie um questionário'>
      <Wrapper>
        <div className='questionary'>
          <div className='questionary-column'>
            <form>
              <StringField
                fieldSchema={{
                  currentMeta: {
                    label: 'Título',
                  },
                  currentRules: {
                    isRequired: true,
                  },
                }}
                value={questionnaireData.title}
                onDirectChange={value => handleInputChange('title', value)}
              />

              <StringField
                fieldSchema={{
                  currentMeta: {
                    label: 'Observações',
                  },
                  currentRules: {
                    isRequired: false,
                  },
                }}
                value={questionnaireData.observation}
                onDirectChange={value => handleInputChange('observation', value)}
              />

              <EnumeratedField
                fieldSchema={publicTargetsEnum}
                value={questionnaireData.targetPublic}
                meta={{
                  placeholder: 'Todos',
                  label: 'Público alvo',
                }}
                onDirectChange={value => handleInputChange('targetPublic', value)}
              />

              <DateField
                label='Data inicial'
                isRequired={true}
                value={questionnaireData.startDate}
                onDirectChange={(value: Date) =>
                  handleInputChange('startDate', value ? format(value, 'yyyy-MM-dd') : null)
                }
                minDate={new Date()}
              />

              <DateField
                label='Data final'
                value={questionnaireData.endDate}
                onDirectChange={(value: Date | null) =>
                  handleInputChange('endDate', value ? format(value, 'yyyy-MM-dd') : null)
                }
                minDate={new Date()}
              />

              <StringField
                fieldSchema={{
                  currentMeta: {
                    label: 'Isenção de responsabilidade (LGPD)',
                    lines: 4,
                  },
                  currentRules: {
                    isRequired: true,
                  },
                }}
                value={questionnaireData.lgpdDisclaimer}
                onDirectChange={value => handleInputChange('lgpdDisclaimer', value)}
              />
            </form>

            <FormQuestion
              formFields={formFields}
              onChange={handleFormFieldsChange}
              setFormFields={setFormFields}
              newField={newField}
              setNewField={setNewField}
            />
          </div>
        </div>
        <div className='button-questionary'>
          <Button
            onClick={e => {
              handleSubmit(e, questionnaireData)
            }}
          >
            Criar Questionário
          </Button>
        </div>
      </Wrapper>
    </Card>
  )
}

export default CreateQuestionnaire
